import { EnvoiDocument, NOM_TYPE_DOCUMENT } from '@/types/envoi.types';
import { Check, ChevronDown, X } from 'lucide-react';

interface LigneEnvoiProps {
  envoi: EnvoiDocument;
  montrerDate?: boolean;
  montrerExpediteur: boolean;
  montrerType?: boolean;
}

/** Une preuve d'envoi, affichée seulement quand elle apporte une information. */
function LigneEnvoi({
  envoi,
  montrerDate = false,
  montrerExpediteur,
  montrerType = false,
}: LigneEnvoiProps) {
  const echec = envoi.statut === 'echec';

  return (
    <div className="py-1">
      <div
        className={`flex min-h-9 items-center gap-2 rounded-md px-2 py-1 text-sm ${
          echec ? 'bg-error-50' : 'hover:bg-neutral-50'
        }`}
      >
        {echec ? (
          <X aria-hidden="true" className="text-error-600 h-4 w-4 shrink-0" />
        ) : (
          <Check
            aria-hidden="true"
            className="text-success-600 h-4 w-4 shrink-0"
          />
        )}
        <span className="sr-only">{echec ? 'Échec :' : 'Envoyé :'}</span>
        {montrerType && (
          <span className="shrink-0 text-xs font-medium text-neutral-600">
            {NOM_TYPE_DOCUMENT[envoi.typeDocument]}
          </span>
        )}
        <span
          className={`min-w-0 flex-1 break-all ${
            echec ? 'text-error-700' : 'text-neutral-900'
          }`}
        >
          {envoi.destinataire}
        </span>
        {montrerDate && (
          <span className="shrink-0 text-xs text-neutral-500 tabular-nums">
            {new Date(envoi.envoyeLe).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
      </div>
      {montrerExpediteur && (
        <p className="px-2 pb-1 pl-8 text-xs break-all text-neutral-500">
          depuis {envoi.expediteur}
        </p>
      )}
      {envoi.messageErreur && (
        <p className="text-error-700 px-2 pb-1 pl-8 text-xs">
          {envoi.messageErreur}
        </p>
      )}
    </div>
  );
}

function libelleNombre(
  nombre: number,
  singulier: string,
  pluriel: string
): string {
  return `${nombre} ${nombre === 1 ? singulier : pluriel}`;
}

/**
 * Les destinataires d'une exécution. Les erreurs restent visibles ; les succès
 * sont disponibles à la demande pour ne pas alourdir chaque exécution.
 */
export function EnvoisDeLExecution({ envois }: { envois: EnvoiDocument[] }) {
  if (envois.length === 0) return null;

  const echecs = envois.filter(envoi => envoi.statut === 'echec');
  const succes = envois.filter(envoi => envoi.statut === 'envoye');
  const expediteurs = new Set(envois.map(envoi => envoi.expediteur));
  const expediteurCommun = expediteurs.size === 1 ? envois[0].expediteur : null;

  return (
    <div className="mt-4 border-t border-neutral-100 pt-2">
      {echecs.length > 0 && (
        <div aria-label="Envois en échec">
          <p className="text-error-700 px-2 py-1 text-sm font-medium">
            {libelleNombre(echecs.length, 'envoi en échec', 'envois en échec')}
          </p>
          {echecs.map(envoi => (
            <LigneEnvoi key={envoi.uuid} envoi={envoi} montrerExpediteur />
          ))}
        </div>
      )}

      {succes.length > 0 && (
        <details className="group">
          <summary className="focus-visible:ring-primary-600 flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-md px-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:outline-none [&::-webkit-details-marker]:hidden">
            <Check
              aria-hidden="true"
              className="text-success-600 h-4 w-4 shrink-0"
            />
            <span className="flex-1">
              {libelleNombre(succes.length, 'envoi accepté', 'envois acceptés')}
            </span>
            <ChevronDown
              aria-hidden="true"
              className="h-4 w-4 shrink-0 text-neutral-400 transition-transform group-open:rotate-180"
            />
          </summary>
          <div className="pb-1">
            {expediteurCommun && (
              <p className="px-2 pb-1 pl-8 text-xs break-all text-neutral-500">
                Envoyé depuis {expediteurCommun}
              </p>
            )}
            {succes.map(envoi => (
              <LigneEnvoi
                key={envoi.uuid}
                envoi={envoi}
                montrerExpediteur={!expediteurCommun}
              />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

/** Les envois manuels ou plus anciens, masqués tant qu'ils ne sont pas utiles. */
export function AutresEnvois({ envois }: { envois: EnvoiDocument[] }) {
  if (envois.length === 0) return null;

  const echecs = envois.filter(envoi => envoi.statut === 'echec');
  const succes = envois.filter(envoi => envoi.statut === 'envoye');

  return (
    <section aria-labelledby="autres-envois-titre">
      <h3
        id="autres-envois-titre"
        className="mb-3 text-lg font-semibold text-neutral-900"
      >
        Autres envois
      </h3>

      {echecs.length > 0 && (
        <div className="mb-2" aria-label="Autres envois en échec">
          {echecs.map(envoi => (
            <LigneEnvoi
              key={envoi.uuid}
              envoi={envoi}
              montrerDate
              montrerExpediteur
              montrerType
            />
          ))}
        </div>
      )}

      {succes.length > 0 && (
        <details className="group border-t border-neutral-100">
          <summary className="focus-visible:ring-primary-600 flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-md px-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:outline-none [&::-webkit-details-marker]:hidden">
            <Check
              aria-hidden="true"
              className="text-success-600 h-4 w-4 shrink-0"
            />
            <span className="flex-1">
              {libelleNombre(succes.length, 'autre envoi', 'autres envois')}
            </span>
            <ChevronDown
              aria-hidden="true"
              className="h-4 w-4 shrink-0 text-neutral-400 transition-transform group-open:rotate-180"
            />
          </summary>
          <div className="pb-1">
            {succes.map(envoi => (
              <LigneEnvoi
                key={envoi.uuid}
                envoi={envoi}
                montrerDate
                montrerExpediteur
                montrerType
              />
            ))}
          </div>
        </details>
      )}
    </section>
  );
}
